import { useState } from "react";

import { type ApiGatewayWorkbenchGeneratedApiKeyResponseData } from "@vega-api/generated/types/apiGatewayWorkbenchGeneratedApiKeyResponseData";
import { CopyButton } from "@vega-core/components/Buttons/CopyButton/CopyButton";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import {
  CheckboxControl,
  CheckboxControlType,
} from "@vega-core/components/Form/Controls/Checkbox/CheckboxControl";
import { Notice } from "@vega-core/components/Notice/Notice";
import { m } from "@vega-core/paraglide/messages";
import { formatDate } from "@vega-core/utils/formatDate";

/**
 * One-time plaintext reveal. The parent modal must keep Esc/click-outside
 * dismissal disabled while this step is shown; the only way out is the Done
 * button, which stays disabled until the admin explicitly acknowledges having
 * stored/shared the key securely.
 */
export const RevealKeyStep = ({
  generatedKey,
  onDone,
}: {
  generatedKey: ApiGatewayWorkbenchGeneratedApiKeyResponseData;
  onDone: VoidFunction;
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <div className="flex flex-col gap-4 text-left">
      <Notice
        content={m.settings_reportingApiKeys_reveal_shownOnce()}
        title={m.settings_reportingApiKeys_reveal_title()}
        type="warning"
        variant="accent"
      />
      <div className="flex items-center gap-2 rounded border border-border/primary bg-background/secondary p-3">
        <code
          className="body-base-regular break-all flex-1"
          data-testid="reporting-api-keys-plaintext"
        >
          {generatedKey.apiKey}
        </code>
        <CopyButton text={generatedKey.apiKey} />
      </div>
      <div className="body-base-regular text-text/secondary">
        {m.settings_reportingApiKeys_reveal_expiresAt({
          expiresAt: formatDate(new Date(generatedKey.expiresAt), true),
        })}
      </div>
      <p className="body-base-regular text-text/secondary">
        {m.settings_reportingApiKeys_reveal_shareSecurely()}
      </p>
      <CheckboxControl
        checked={acknowledged}
        label={m.settings_reportingApiKeys_reveal_acknowledge()}
        name="reporting-api-keys-reveal-ack"
        onBlur={() => {}}
        onChange={setAcknowledged}
        type={CheckboxControlType.ACKNOWLEDGE}
      />
      <ThemedButton
        dataTestId="reporting-api-keys-reveal-done"
        disabled={!acknowledged}
        onClick={onDone}
      >
        {m.settings_reportingApiKeys_reveal_done()}
      </ThemedButton>
    </div>
  );
};
